<template>
    <div>
        <ul class="stop-list no-bullet" :class="service.class">
            <li v-for="(stop, i) in stops" :key="i" class="stop-item">
                <RouterLink
                    :to="{
                        name: 'route-stop',
                        params: {
                            route: route.noParcours,
                            stop: `${stop.noArret}` + (stop.nom ? `-${slugify(stop.nom)}` : ''),
                            direction: directionSlug(direction.code, direction.description),
                            date: !isToday(selectedDate) ? selectedDate : getEffectiveDate(new Date(), 'yyyy-MM-dd'),
                        },
                    }"
                >
                    <span v-if="stop.arretNonDesservi" class="visuallyhidden">{{ t('legend.unserved_stop') }}</span>
                    <div class="stop-infos">
                        <div>
                            <span class="stop-name">{{ stop.noArret }} {{ stop.nom ? ' - ' + stop.nom : '' }}</span
                            ><span class="no-wrap">&nbsp;<IconChevron width="6" height="10" /></span>
                        </div>
                        <div>
                            <img
                                v-if="route.accessible && stop.accessible"
                                class="accessible-icon"
                                src="@@/img/accessible.svg"
                            />
                            <span v-if="route.accessible && stop.accessible" class="visuallyhidden">
                                {{ t('a11y.accessible.accessible_stop') }}
                            </span>
                            <span v-else class="visuallyhidden">{{ t('a11y.accessible.not_accessible_stop') }}</span>
                        </div>
                    </div>
                    <small class="stop-description">{{ stop.description }}</small>
                </RouterLink>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

import { format } from '@@/js/utils/dateFns';
import { useSchedulesStore } from '@@/js/stores/schedules';
import { useEffectiveDate } from '@/schedules/composables/useEffectiveDate';
import directionSlug from '@@/js/utils/directionSlug';
import slugify from '@@/js/utils/slugify';

import IconChevron from '@@/icons/chevron.svg';

const props = defineProps({
    stops: {
        type: Array,
        default: () => [],
    },
    route: {
        type: Object,
        default: () => ({}),
    },
    accessible: {
        type: Boolean,
        default: true,
    },
    direction: {
        type: Object,
        default: () => ({}),
    },
    selectedDate: {
        type: String,
        default: '',
    },
});

const { t } = useI18n({ useScope: 'global' });
const { getEffectiveDate, isToday } = useEffectiveDate();
const schedulesStore = useSchedulesStore();
const { getServiceType } = schedulesStore;
const service = computed(() => getServiceType(props.route.codeTypeService));
</script>

<style lang="scss" scoped>
.stop-list {
    margin: 0;
}
@include colorize {
    .stop-item {
        color: get-color-variation('base');
    }
}
.stop-item {
    position: relative;
    margin-left: 24px;
    padding: 16px;
    border-bottom: 1px solid $color-black-10;
    &::before {
        content: '';
        display: inline-block;
        position: absolute;
        top: 20px;
        left: -24px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: currentColor;
    }
    &::after {
        content: '';
        display: inline-block;
        position: absolute;
        top: 24px;
        left: -19px;
        width: 6px;
        height: 100%;
        background-color: currentColor;
    }
    &.is-unserved::before {
        content: '';
        z-index: 1;
        position: absolute;
        top: 10px;
        left: -15px;
        width: 28px;
        height: 28px;
        background-color: transparent;
        background-image: url('@@/img/unserved-stop.svg');
        @include breakpoint(medium) {
            top: 18px;
        }
    }
    &:last-of-type {
        border-bottom: none;
        &::after {
            display: none;
        }
    }
    a {
        display: block;
        text-decoration: none;
    }
}
.stop-infos {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.stop-name {
    @include rem-typo(18px);
    color: $color-blue-60;
}
.stop-description {
    @include rem-typo(12px);
    display: block;
    color: $color-black-50;
}
.accessible-icon {
    margin-left: 8px;
    width: 18px;
    height: 18px;
}
</style>
